import {Component, EnvironmentInjector, inject, Injector, OnInit, runInInjectionContext} from '@angular/core';
import {NavigationEnd, Router, RouterLink} from '@angular/router';

import {TranslateModule} from '@ngx-translate/core';
import {filter, take} from 'rxjs';
import {AResourceService} from '../../../services/resources/a-resource.service';
import {CustomNotificationService} from '../../../services/utils/custom-notification.service';
import {SpinnerComponent} from '../spinner/spinner.component';
import {toObservable} from '@angular/core/rxjs-interop';
import {BreadcrumbService} from '../../../services/utils/breadcrumb.service';
import {FormatHelper} from '../../../helpers/format-helper';
import {ModelService} from '../../../services/utils/model.service';
import {VehicleService} from '../../../services/resources/vehicle.service';
import {SettingService} from '../../../services/resources/setting.service';
import {LocationService} from '../../../services/resources/location.service';
import {EmployeeService} from "../../../services/resources/employee.service";
import {RouteService} from "../../../services/resources/route.service";

@Component({
  selector: 'app-breadcrumbs',
  imports: [RouterLink, TranslateModule, SpinnerComponent],
  templateUrl: './breadcrumbs.component.html',
  standalone: true,
  styleUrl: './breadcrumbs.component.scss',
})
export class BreadcrumbsComponent implements OnInit {
  routePaths: string[] = [];
  private environmentInjector = inject(EnvironmentInjector);
  baseParents: string[] = [
    'vehicles',
    'settings',
    'locations',
    'meeting-points',
    'employees',
    'routes',
    'stops'
  ];
  validParentPaths: string[] = [
    'vehicles',
    'settings',
    'locations',
    'meeting-points',
    'employees',
    'routes',
    'stops'
  ];

  constructor(
    private router: Router,
    private notificationService: CustomNotificationService,
    public breadcrumbService: BreadcrumbService,
    private injector: Injector,
    private modelService: ModelService,
  ) {}

  ngOnInit() {
    this.setRoutePaths();

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe({
        next: () => {
          this.setRoutePaths();
        },
      });
  }

  setRoutePaths() {
    this.routePaths = this.router.url.split('/').filter((p) => p != '');
    this.initBreadcrumbs();
  }

  initBreadcrumbs() {
    let parents: {
      text: string;
      path: string;
      service: AResourceService<any>;
    }[] = [];
    let parent: {
      text: string;
      path: string;
      service: AResourceService<any>;
      identifier: string;
    } | null = null;
    let currentPath = '';

    this.breadcrumbService.reset();

    this.routePaths.forEach((path) => {
      let text = '';
      let loading = false;
      let newParent;
      let isSubParent = false;
      if ((newParent = this.isNotConcreteModel(path))) {
        parent = newParent;

        isSubParent = this.isSubParent(parents, path);
        parents.push(newParent);

        text = `NAVIGATION.${FormatHelper.toSnakeCase(newParent.text).toUpperCase()}`;
        newParent.path = currentPath + `/${newParent.path}`;
      } else {
        // get concrete model
        if (parent) {
          // Check if the path is a number before assuming it's a model ID
          const id = Number(path);
          if (!Number.isInteger(id)) {
            // It's likely a sub-page like "stock-bookings" – allow it
            parent = null;
            currentPath += `/${path}`;
            this.breadcrumbService.add({
              text: `NAVIGATION.${path.toUpperCase().replace(/-/g, '_')}`,
              path: currentPath,
              loading: false,
            });
            return;
          }
          let concreteModel = this.getConcreteModel(id, parent);
          if (concreteModel) {
            text = this.modelService.getModelLabel(
              concreteModel,
              parent.identifier,
            );
          } else {
            text = parent.text;
            loading = true;
          }
        } else {
          this.handleInvalidParentModel();
          return;
        }
      }

      currentPath += `/${path}`;

      if (!isSubParent) {
        this.breadcrumbService.add({
          text: text,
          path: currentPath,
          loading: loading,
        });
      }
    });
  }

  isSubParent(parents: any[], path: string) {
    let isSub = false;

    if (this.baseParents.find((bp) => bp == path) != null) return isSub;

    parents.forEach((p) => {
      if (this.baseParents.find((bp) => bp == p.text.toLowerCase()) != null)
        isSub = true;
    });
    return isSub;
  }

  isNotConcreteModel(path: string) {
    if (this.validParentPaths.indexOf(path) === -1) return null;

    // TODO: DASHBOARD NEEDS TO BE HANDLED SPECIFICALLY

    let service;
    let identifier = '';
    switch (path) {
      case 'vehicles':
        service = this.injector.get(VehicleService);
        identifier = 'vehicles';
        break;
      case 'settings':
        service = this.injector.get(SettingService);
        identifier = 'settings';
        break;
      case 'locations':
        service = this.injector.get(LocationService);
        identifier = 'locations';
        break;
      case 'meeting-points':
        service = this.injector.get(LocationService);
        identifier = 'meetingPoints';
        break;
      case 'employees':
        service = this.injector.get(EmployeeService);
        identifier = 'employees';
        break;
      case 'routes':
        service = this.injector.get(RouteService);
        identifier = 'routes';
        break;
      case 'stops':
        service = this.injector.get(RouteService)
        identifier = 'stops'
        break;
    }
    // if(path != "dashboard")
    //   service!.getAll()
    return {
      text: path.toUpperCase(),
      identifier: identifier,
      path: path,
      service: service as AResourceService<any>,
    };
  }

  getConcreteModel(
    id: number,
    parent: {
      text: string;
      path: string;
      service: AResourceService<any>;
      identifier: string;
    },
  ) {
    let model = null;
    model =
      parent.service.models().find((m) => m.id == id) ??
      parent.service.usedConcreteModels().find((m) => m.id == id);

    if (!model) {
      if (parent.service.loading().loading) {
        runInInjectionContext(this.environmentInjector, () => {
          toObservable(parent.service.loading)
            .pipe(
              filter((v) => !v.loading),
              take(1),
            )
            .subscribe({
              next: () => {
                model =
                  parent.service.models().find((m) => m.id == id) ??
                  parent.service.usedConcreteModels().find((m) => m.id == id);
                if (model)
                  this.breadcrumbService.setLoadedBreadcrumb(
                    model,
                    this.modelService.getModelLabel(model, parent.identifier),
                    parent.text,
                  );
                else this.fetchConcreteModel(id, parent);
              },
              error: () => {
                this.handleInvalidParentModel();
              },
            });
        });
      } else this.fetchConcreteModel(id, parent);
    }
    return model;
  }

  fetchConcreteModel(
    id: number,
    parent: {
      text: string;
      path: string;
      service: AResourceService<any>;
      identifier: string;
    },
  ) {
    parent.service.getOne(id).subscribe({
      next: (model) => {
        this.breadcrumbService.setLoadedBreadcrumb(
          model,
          this.modelService.getModelLabel(model, parent.identifier),
          parent.text,
        );
      },
      error: () => {
        this.handleInvalidConcreteModel(parent);
      },
    });
  }

  handleInvalidParentModel() {
    this.router
      .navigate(['/'])
      .then(() => this.notificationService.error('PAGE_NOT_FOUND'));
  }

  handleInvalidConcreteModel(parent: {
    text: string;
    path: string;
    service: AResourceService<any>;
  }) {
    this.router
      .navigate([parent.path])
      .then(() =>
        this.notificationService.error(
          `NAVIGATION.${parent.text}_RESOURCE_NOT_FOUND`,
        ),
      );
  }
}
